import { Provenance, useCan } from '../features/agency/ui';
import { useState, useCallback, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  useAssignmentDetail,
  useDownloadStats,
  ASSIGNMENT_STATUS_LABELS,
  APPLE_ID_MATCH_RESULT_LABELS,
} from '../features/assignments';
import type { AppleIdMatchResult, AssignmentStatus } from '../features/assignments';
import { DownloadEvidenceCard } from '../features/assignments/components/DownloadEvidenceCard';
import { DownloadTimeline } from '../features/assignments/components/DownloadTimeline';
import { DownloadStatsPanel } from '../features/assignments/components/DownloadStatsPanel';
import { MessageReviewCard } from '../features/assignments/components/MessageReviewCard';
import { ConfirmDialog } from '../components/feedback/ConfirmDialog';
import { ImagePreviewGrid } from '../components/media/ImagePreviewGrid';
import { parseSubmissionEvidence } from '../features/assignments/utils/submissionEvidence';
import {
  resolveAdminImageOriginalUrl,
  resolveAdminImagePreviewUrl,
  resolveAdminImageThumbnailUrl,
} from '../lib/upload/cos';
import type { AssignmentOCRCheckDTO } from '../features/assignments/types/assignments.types';

const STATUS_STYLES: Record<AssignmentStatus, string> = {
  assigned: 'bg-blue-50 text-blue-700',
  submitted: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-700',
  settled: 'bg-green-50 text-green-700',
  expired: 'bg-gray-100 text-gray-600',
};

const APPLE_ID_MATCH_BADGE: Record<AppleIdMatchResult, string> = {
  unchecked: 'bg-gray-100 text-gray-600',
  checking: 'bg-amber-50 text-amber-700',
  matched: 'bg-green-50 text-green-700',
  mismatch: 'bg-red-50 text-red-700',
  failed: 'bg-amber-50 text-amber-700',
};

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

type DialogState =
  | { type: 'closed' }
  | { type: 'approve' }
  | { type: 'reject' };

export function AssignmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const assignmentId = Number(id);
  const canOriginal = useCan('agent_evidence.original.view');
  const {
    assignment,
    loading,
    error,
    reviewing,
    review,
    messageReview,
    messageReviewLoading,
    messageReviewError,
    messageActionLoading,
    retryMessageReview,
    replaceMessageItem,
    refreshImages,
  } = useAssignmentDetail(assignmentId);

  const hasDownloadRecords = (assignment?.download_records?.length ?? 0) > 0;
  const hasDownloadData = !!assignment?.download_evidence || hasDownloadRecords;
  const { stats, loading: statsLoading, error: statsError, reload: statsReload } = useDownloadStats(
    hasDownloadData ? assignment?.task_id : undefined,
  );

  const [dialog, setDialog] = useState<DialogState>({ type: 'closed' });

  const handleConfirm = useCallback((note: string) => {
    if (dialog.type === 'closed') return;
    review(dialog.type === 'approve' ? 'approve' : 'reject', note);
    setDialog({ type: 'closed' });
  }, [dialog, review]);

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Error
  if (error || !assignment) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-red-500 mb-3">{error ?? '审核记录不存在'}</p>
        <Link to="/assignments" className="text-sm text-primary hover:underline">返回列表</Link>
      </div>
    );
  }

  const status = assignment.status as AssignmentStatus;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Link to="/assignments" className="hover:text-primary transition-colors">会员提交审核</Link>
        <span>/</span>
        <span className="text-gray-900">审核详情</span>
      </div>

      {/* Basic info */}
      <section className="space-y-3">
        <h3 className="text-base font-medium text-gray-900">基础信息</h3>
        <div className="border border-gray-200 rounded-md px-4">
          <InfoRow label="审核 ID" value={String(assignment.id)} mono />
          <InfoRow label="任务" value={`#${assignment.task_id} ${assignment.task_title}`} />
          <InfoRow label="任务来源"><Provenance item={{...assignment,claim_revision_id:assignment.claim_snapshot?.revision_id}}/></InfoRow>
          {assignment.claim_snapshot?.reward_amount_minor!=null&&<InfoRow label="领取时奖励快照" value={`¥${(assignment.claim_snapshot.reward_amount_minor/100).toFixed(2)}`}/>}
          {assignment.attempt_no!=null&&<InfoRow label="提交尝试次数" value={String(assignment.attempt_no)}/>}
          <InfoRow label="用户" value={`#${assignment.user_id} ${assignment.user_nickname}`} />
          <InfoRow label="用户 App 版本" value={assignment.latest_app_version || '-'} mono />
          <InfoRow label="状态">
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'}`}>
              {ASSIGNMENT_STATUS_LABELS[status] ?? assignment.status}
            </span>
          </InfoRow>
          <InfoRow label="Apple ID 匹配">
            <AppleIdMatchBadge result={assignment.apple_id_match_result} />
          </InfoRow>
          {assignment.task_type !== 'message_send' && assignment.evidence && (!assignment.agent_portal_task_id || canOriginal) && <EvidenceRow raw={assignment.evidence} />}
          {assignment.review_note && (
            <InfoRow label="审核备注">
              <span className={`text-sm ${status === 'rejected' ? 'text-red-600' : 'text-gray-900'}`}>
                {assignment.review_note}
              </span>
            </InfoRow>
          )}
          <InfoRow label="提交时间" value={formatDateTime(assignment.submitted_at)} />
          <InfoRow label="审核时间" value={formatDateTime(assignment.reviewed_at)} />
          <InfoRow label="创建时间" value={formatDateTime(assignment.created_at)} />
        </div>
      </section>

      {/* Download evidence */}
      {assignment.download_evidence && (
        <DownloadEvidenceCard
          evidence={assignment.download_evidence}
          app={assignment.download_app}
        />
      )}

      {assignment.task_type === 'message_send' && (
        <MessageReviewCard
          onRefreshImages={refreshImages}
          detail={assignment.agent_portal_task_id&&!canOriginal&&messageReview?{...messageReview,evidence:[]}:messageReview}
          loading={messageReviewLoading}
          error={messageReviewError}
          onApprove={(note, auditReason) => review('approve', note, auditReason)}
          onReject={(note, auditReason) => review('reject', note, auditReason)}
          onRetry={retryMessageReview}
          onReplace={replaceMessageItem}
          actionLoading={messageActionLoading}
          reviewing={reviewing}
        />
      )}

      {/* Download timeline */}
      {assignment.download_records && assignment.download_records.length > 0 && (
        <DownloadTimeline records={assignment.download_records} />
      )}

      {/* Download stats */}
      {hasDownloadData && (
        <DownloadStatsPanel
          stats={stats}
          loading={statsLoading}
          error={statsError}
          onRetry={statsReload}
        />
      )}

      {assignment.ocr_checks && assignment.ocr_checks.length > 0 && (
        <OCRChecksPanel checks={assignment.ocr_checks} />
      )}

      {/* Review actions */}
      {status === 'submitted' && assignment.task_type !== 'message_send' && (
        <section className="space-y-3">
          <h3 className="text-base font-medium text-gray-900">审核操作</h3>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDialog({ type: 'approve' })}
              disabled={reviewing}
              className="px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 transition-colors"
            >
              通过
            </button>
            <button
              onClick={() => setDialog({ type: 'reject' })}
              disabled={reviewing}
              className="px-4 py-2 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-40 transition-colors"
            >
              拒绝
            </button>
          </div>
        </section>
      )}

      {/* Back link */}
      <div className="pt-2 border-t border-gray-200">
        <Link to="/assignments" className="text-sm text-primary hover:underline">
          &larr; 返回审核列表
        </Link>
      </div>

      {/* Dialogs */}
      <ConfirmDialog
        open={dialog.type === 'approve'}
        title="确认通过审核"
        description="通过后将触发奖励结算，此操作不可撤销。"
        confirmLabel="通过"
        variant="primary"
        loading={reviewing}
        noteLabel="备注（可选）"
        onConfirm={handleConfirm}
        onCancel={() => setDialog({ type: 'closed' })}
      />
      <ConfirmDialog
        open={dialog.type === 'reject'}
        title="确认拒绝"
        description="拒绝后用户需重新提交，请填写原因。"
        confirmLabel="拒绝"
        variant="danger"
        loading={reviewing}
        noteLabel="拒绝原因"
        noteRequired
        onConfirm={handleConfirm}
        onCancel={() => setDialog({ type: 'closed' })}
      />
    </div>
  );
}

function InfoRow({ label, value, mono, children }: {
  label: string;
  value?: string;
  mono?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex items-start py-2 border-b border-gray-100 last:border-b-0">
      <span className="w-28 shrink-0 text-xs text-gray-500">{label}</span>
      {children ?? (
        <span className={`text-sm text-gray-900 break-all ${mono ? 'font-mono tabular-nums' : ''}`}>{value || '-'}</span>
      )}
    </div>
  );
}

function AppleIdMatchBadge({ result }: { result?: AppleIdMatchResult }) {
  const normalized = result ?? 'unchecked';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${APPLE_ID_MATCH_BADGE[normalized] ?? APPLE_ID_MATCH_BADGE.unchecked}`}>
      {APPLE_ID_MATCH_RESULT_LABELS[normalized] ?? normalized}
    </span>
  );
}

function OCRChecksPanel({ checks }: { checks: AssignmentOCRCheckDTO[] }) {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-medium text-gray-900">Apple ID OCR 记录</h3>
      <div className="space-y-3">
        {checks.map((check) => (
          <div
            key={`${check.assignment_id}-${check.image_index}-${check.id ?? check.request_id ?? ''}`}
            className="rounded-md border border-gray-200 bg-white p-4"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-900">截图 {check.image_index + 1}</span>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${ocrStatusClass(check.status)}`}>
                {ocrStatusLabel(check.status)}
              </span>
              <span className="text-xs text-gray-500">{check.provider || '-'}</span>
              {check.request_id && (
                <span className="font-mono text-xs text-gray-400">{check.request_id}</span>
              )}
            </div>
            <div className="mt-3 grid gap-2 text-sm text-gray-700">
              <div>
                <span className="text-xs text-gray-500">图片路径：</span>
                <span className="font-mono break-all">{check.image_path || '-'}</span>
              </div>
              <div>
                <span className="text-xs text-gray-500">识别账号：</span>
                <span className="break-all">
                  {check.extracted_accounts && check.extracted_accounts.length > 0
                    ? check.extracted_accounts.join('、')
                    : '-'}
                </span>
              </div>
              {check.failure_reason && (
                <div>
                  <span className="text-xs text-gray-500">失败原因：</span>
                  <span className="text-red-600">{check.failure_reason}</span>
                </div>
              )}
              {check.text_lines && check.text_lines.length > 0 && (
                <div>
                  <span className="text-xs text-gray-500">OCR 文本：</span>
                  <div className="mt-1 rounded-md bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700">
                    {check.text_lines.map((line, index) => (
                      <div key={`${check.image_index}-${index}`} className="break-all">{line}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ocrStatusLabel(status: AssignmentOCRCheckDTO['status']): string {
  switch (status) {
    case 'checking':
      return '校验中';
    case 'matched':
      return '一致';
    case 'not_found':
      return '未识别';
    case 'skipped':
      return '已跳过';
    case 'mismatch':
      return '不一致';
    case 'failed':
      return '识别失败';
    default:
      return status;
  }
}

function ocrStatusClass(status: AssignmentOCRCheckDTO['status']): string {
  switch (status) {
    case 'checking':
      return 'bg-amber-50 text-amber-700';
    case 'matched':
      return 'bg-green-50 text-green-700';
    case 'not_found':
    case 'mismatch':
      return 'bg-red-50 text-red-700';
    case 'failed':
      return 'bg-amber-50 text-amber-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function EvidenceRow({ raw }: { raw: string }) {
  const evidence = parseSubmissionEvidence(raw);
  const imagePreviews = evidence.imageUrls.map((src) => {
    const fallbackSrc = resolveAdminImagePreviewUrl(src);
    const thumbSrc = resolveAdminImageThumbnailUrl(src, { width: 360, height: 360, quality: 76 });
    const fullSrc = resolveAdminImageOriginalUrl(src);
    return thumbSrc ? { src: thumbSrc, fullSrc, fallbackSrc } : null;
  }).filter((item): item is { src: string; fullSrc: string; fallbackSrc: string } => item !== null);
  const hasStructuredContent =
    evidence.description || imagePreviews.length > 0 || evidence.legacyImagePaths.length > 0;

  return (
    <InfoRow label="提交证据">
      {hasStructuredContent ? (
        <div className="min-w-0 flex-1 space-y-3">
          {evidence.description && (
            <p className="whitespace-pre-wrap break-words text-sm text-gray-900">{evidence.description}</p>
          )}
          {imagePreviews.length > 0 && (
            <ImagePreviewGrid images={imagePreviews} altPrefix="提交截图" />
          )}
          {evidence.legacyImagePaths.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <p>旧版提交的是客户端本地路径，后台无法直接读取图片：</p>
              {evidence.legacyImagePaths.map((path, idx) => (
                <p key={`${path}-${idx}`} className="mt-1 break-all font-mono">{path}</p>
              ))}
            </div>
          )}
        </div>
      ) : (
        <span className="text-sm text-gray-900 break-all">{raw}</span>
      )}
    </InfoRow>
  );
}
